import { endOnboardingJourney } from "analytics/onboarding";
import Modal from "common/Modal/Modal";
import { OnboardingStyles } from "features/onboarding/common/styles";
import React, { useContext } from "react";
import { useHistory } from "react-router-dom";
import {
  OnboardingDetailedStep,
  SharedSkipOnboardingModalProps,
} from "@shared/features/onboarding/types";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import {
  OnboardingStepValueType,
  TradingMethodTypeValueType,
} from "analytics/events";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { getAnalyticsBotSource, startIntercomChecklist } from "utils/misc";
import { useBottomNavigator } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ONBOARDING_CONFIRM_SKIP_BUTTON_ID } from "testIds.ts";
import styled from "styled-components";
import Button from "common/Buttons/Button";
import { newLookType } from "common/NewLook/tokens";

interface SkipOnboardingModalProps extends SharedSkipOnboardingModalProps {
  onboardingStep: OnboardingStepValueType;
  tradingMethodType?: TradingMethodTypeValueType;
}

const SkipOnboardingModal = ({
  isVisible,
  content,
  onCancel,
  newPath,
  onboardingStep,
  tradingMethodType,
}: SkipOnboardingModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const { changeStep } = useContext(OnboardingContext) || {};
  const bottomNavigatorData = useBottomNavigator();

  const skipOnboarding = () => {
    changeStep?.(OnboardingDetailedStep.SKIP);
    analytics?.onboardingSkipped({
      onboardingStep,
      tradingMethodType,
      botSource: getAnalyticsBotSource(),
    });
    endOnboardingJourney();
    history.push(newPath);
    if (!bottomNavigatorData.baseShouldDisplay) {
      startIntercomChecklist(27879301);
    }
  };

  return (
    <Modal
      open={isVisible}
      footer={false}
      width={420}
      styles={{ body: { padding: 24 } }}
      onCancel={onCancel}
    >
      <OnboardingStyles.ModalTitle style={{ margin: "0 32px 8px 0" }}>
        {t("skip_modal.title")}
      </OnboardingStyles.ModalTitle>
      <ModalBody>{content}</ModalBody>
      <ModalActions>
        <Button
          onClick={skipOnboarding}
          data-testid={ONBOARDING_CONFIRM_SKIP_BUTTON_ID}
        >
          {t("skip_modal.yes_button")}
        </Button>
        <Button type="primary" onClick={onCancel}>
          {t("skip_modal.no_button")}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default SkipOnboardingModal;

const ModalBody = styled.div`
  margin: 0;
  ${newLookType.body};
  color: ${(p) => p.theme.colors.secondaryText};
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    > .ant-btn {
      width: 100%;
    }
  }
`;
